import { useAtomValue } from 'jotai';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { allEventsQueryAtom, eventKolsAtom, isAdminAtom } from 'r/atoms';
import { ProfileContext } from 'r/contexts';
import type { Profile } from 'r/types';
import { Avatar, SpinnerCircle } from 'r/ui';
import { unionContexts } from 'r/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  formatDate,
  formatHitRate,
  HitMissCounts,
  NewKolBadge,
} from '../shared';
import KolTweets from './KolTweets';
import KolAnalyses from './KolAnalyses';
import KolEvents from './KolEvents';
import KolStats from './KolStats';

const VIEWS = ['tweets', 'analyses', 'events', 'stats'] as const;
type View = (typeof VIEWS)[number];

export default unionContexts(KolPage, [ProfileContext, 'profile']);

function KolPage({ profile }: { profile: Profile | null }) {
  const { kolId } = useParams<{ kolId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = useAtomValue(isAdminAtom);
  const eventsQuery = useAtomValue(allEventsQueryAtom);
  const kols = useAtomValue(eventKolsAtom);

  const requestedView = searchParams.get('view') as View | null;
  const view: View =
    requestedView && VIEWS.includes(requestedView) ? requestedView : 'tweets';

  if (!isAdmin || !profile) {
    return (
      <div className="py-8 text-center text-secondary">
        {!profile ? 'Please login' : 'You are not admin'}
      </div>
    );
  }

  const kol = kols?.find((k) => k.kolID === kolId);

  return (
    <div className="max-w-7xl w-full mx-auto py-10 flex flex-col gap-6">
      <Link
        to="/?tab=kols"
        className="inline-flex items-center gap-1 text-sm text-secondary hover:text-main w-fit"
      >
        <ArrowLeft className="w-4 h-4" /> All KOLs
      </Link>

      {!kols ?
        <div className="flex justify-center p-10">
          {eventsQuery.isError ?
            <span className="text-secondary">Failed to load events</span>
          : <SpinnerCircle size={24} color="var(--muted-foreground)" />}
        </div>
      : !kol ?
        <div className="py-8 text-center text-secondary">
          No SanPlay events for this author
        </div>
      : <>
          <div className="bg-background rounded-2xl p-6 shadow-sm border border-bg-accent-tabs flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-4 min-w-0">
              <Avatar
                size={56}
                user={{ id: kol.username, avatar: kol.avatar }}
              />
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-main truncate">
                    {kol.name || kol.username}
                  </h2>
                  {kol.isNew && <NewKolBadge />}
                </div>
                <a
                  href={`https://x.com/${kol.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-focus hover:underline w-fit"
                >
                  @{kol.username} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-2 md:ml-auto text-sm">
              <div>
                <dt className="text-xs text-secondary">Events</dt>
                <dd className="font-semibold">
                  {kol.eventsCount}{' '}
                  <span className="font-normal text-secondary">
                    ({kol.activeCount} active)
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-xs text-secondary">H / M / N</dt>
                <dd>
                  <HitMissCounts
                    hits={kol.hits}
                    misses={kol.misses}
                    neutrals={kol.neutrals}
                  />{' '}
                  · {formatHitRate(kol.hitRate)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-secondary">First event</dt>
                <dd className="font-semibold">
                  {formatDate(kol.firstEventAt)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-secondary">Last event</dt>
                <dd className="font-semibold">{formatDate(kol.lastEventAt)}</dd>
              </div>
            </dl>
          </div>

          <Tabs
            value={view}
            onValueChange={(value) =>
              setSearchParams({ view: value }, { replace: true })
            }
          >
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="tweets">Tweets</TabsTrigger>
              <TabsTrigger value="analyses">Analyses</TabsTrigger>
              <TabsTrigger value="events">
                Events ({kol.eventsCount})
              </TabsTrigger>
              <TabsTrigger value="stats">Stats</TabsTrigger>
            </TabsList>
            <TabsContent value="tweets" className="mt-6">
              <KolTweets kol={kol} />
            </TabsContent>
            <TabsContent value="analyses" className="mt-6">
              <KolAnalyses kol={kol} />
            </TabsContent>
            <TabsContent value="events" className="mt-6">
              <KolEvents kol={kol} />
            </TabsContent>
            <TabsContent value="stats" className="mt-6">
              <KolStats kol={kol} />
            </TabsContent>
          </Tabs>
        </>
      }
    </div>
  );
}
